import { Effect, References, Result, Schema } from "effect";
import { ContentIdentityText, Sha256Identity, IdentityObjectInput, IdentityArrayInput, IdentityRecordInput, UniqueIdentityValues, exactIdentityFields } from "../../identity-input.ts";
import type { JsonObject } from "../../canonical-json/model.ts";
import { canonicalJsonStringify as serialize } from "../../canonical-json/serialize.ts";
import { canonicalJsonDigest } from "../../canonical-json/digest.ts";
import { canonicalJsonCause } from "../../canonical-json/cause.ts";
import { BuildArtifactKey, DependencyLayerKey } from "../build-identity/model.ts";
import { developmentIdentifier, type CandidateId, type TreeId } from "../../development-identifiers.ts";
import type { Digest } from "../../digest/service.ts";
import { ContractDecodeError } from "../../errors/validation.ts";
import { CheckManifestDigest, CheckReceiptDigest, CheckTimestamp, CheckAttemptNumber, CheckTerminalState,
  CHECK_MANIFEST_SCHEMA_VERSION, CHECK_RECEIPT_SCHEMA_VERSION, MAX_CHECK_CANONICAL_JSON_BYTES, checkReceiptError,
  type CheckReceiptError, type CheckManifest, type CheckManifestInput, type CheckManifestResult, type CheckReceipt,
  type CheckReceiptInput, type CheckReceiptResult, type CheckScopeInput, type CheckScopeIdentity, type CheckToolchainInput,
  type CheckToolchainIdentity, type CheckCacheability, type CheckEvidenceInput, type CheckEvidenceIdentity } from "./model.ts";

// Reflection and callbacks retain their native evaluation order. Their refusals
// cross the domain boundary privately, including after an accepted digest.
const boundary = <A, R>(program: Effect.Effect<A, CheckReceiptError, R>) =>
  program.pipe(Effect.catchDefect(cause => Effect.fail(checkReceiptError(cause))));
// Capture the named diagnostic once, then build each caller's accepted program.
// The scheduler guard includes diagnostic setup and receiving-domain failures.
const operation = (name: string) => {
  const trace = Effect.fn("CheckReceipt." + name);
  return <A, R>(body: () => Effect.Effect<A, CheckReceiptError, R>): Effect.Effect<A, CheckReceiptError, R> =>
    trace(() => boundary(body()))().pipe(Effect.uninterruptible, Effect.provideService(References.PreventSchedulerYield, true));
};
const parse = <S extends Schema.ConstraintDecoder<unknown>>(schema: S, refusal?: string | ((error: Schema.SchemaError) => unknown)) => {
  const decode = Schema.decodeUnknownResult(schema);
  return (value: unknown): S["Type"] => Result.getOrThrowWith(decode(value), error => Schema.is(Schema.String)(refusal) ? new TypeError(refusal) : refusal ? refusal(error) : new ContractDecodeError({ message: error.message }));
};
const canonicalJsonStringify = (value: unknown) => Result.getOrThrowWith(serialize(value), canonicalJsonCause);
const hash = (value: unknown) => canonicalJsonDigest(value).pipe(Effect.mapError(error => checkReceiptError(canonicalJsonCause(error))));
const decodeCheckManifestDigest = /* @__PURE__ */ (() => parse(CheckManifestDigest))();
const decodeCheckReceiptDigest = /* @__PURE__ */ (() => parse(CheckReceiptDigest))();

const manifestFingerprint = (manifest: () => CheckManifest) => Effect.gen(function* () {
  const canonicalJson = canonicalJsonStringify(manifest());
  const digest = decodeCheckManifestDigest(`check-manifest:v${CHECK_MANIFEST_SCHEMA_VERSION}:sha256:${yield* hash(manifest())}`);
  return { canonicalJson, digest };
});

function compareText(left: string, right: string): number {
  return left < right ? -1 : left > right ? 1 : 0;
}

const objectInput = (value: unknown, label: string) =>
  parse(IdentityObjectInput, `${label} must be an object.`)(value);
const exactObjectKeys = (value: object, required: readonly string[], optional: readonly string[], label: string): void => {
  parse(exactIdentityFields(required, optional),
    `${label} has unsupported or missing fields.`)(value);
};
const jsonEncoder = new TextEncoder();
const SnapshotText = /* @__PURE__ */ (() => Schema.String.check(Schema.makeFilter(value =>
  jsonEncoder.encode(value).byteLength <= MAX_CHECK_CANONICAL_JSON_BYTES
)))();
const snapshotJson = /* @__PURE__ */ (() => parse(Schema.fromJsonString(Schema.Unknown)))();
function jsonSnapshot<Value>(value: Value, label: string): Value {
  return Result.getOrThrowWith(Result.try(() => {
    const canonicalJson = canonicalJsonStringify(value);
    parse(SnapshotText, () => new RangeError(
      `${label} exceeds the ${MAX_CHECK_CANONICAL_JSON_BYTES}-byte limit.`
    ))(canonicalJson);
    return snapshotJson(canonicalJson) as Value;
  }), error => error instanceof RangeError ? error : new TypeError(
    `${label} must be immutable plain JSON: ${error instanceof Error ? error.message : "invalid value"}`
  ));
}
function inputSnapshot<Value extends object>(value: Value, label: string, required: readonly string[], optional: readonly string[] = [], objectLabel = label): Value {
  objectInput(value, objectLabel);
  const snapshot = jsonSnapshot(value, label);
  exactObjectKeys(snapshot, required, optional, label);
  return snapshot;
}
const stableText = (value: unknown, label: string): string =>
  parse(ContentIdentityText, `${label} must be 1-256 trimmed characters without controls.`)(value);
const sha256Identity = (value: unknown, label: string): string =>
  parse(Sha256Identity, `${label} must be a lowercase SHA-256 identity.`)(value);
const checkCacheable = /* @__PURE__ */ (() => parse(Schema.Boolean, "Check cacheable must be a boolean."))();
const noCacheabilityReason = /* @__PURE__ */ (() => parse(Schema.Undefined, "A cacheable check must not have a non-cacheable reason."))();
const nonCacheable = /* @__PURE__ */ (() => parse(Schema.Literal(false), "Check manifest cacheability is invalid."))();
const manifestVersion = /* @__PURE__ */ (() => parse(Schema.Literal(CHECK_MANIFEST_SCHEMA_VERSION), "Check manifest has an unsupported version."))();
const receiptVersion = /* @__PURE__ */ (() => parse(Schema.Literal(CHECK_RECEIPT_SCHEMA_VERSION), "Stored check receipt has an unsupported version."))();
const attemptNumber = /* @__PURE__ */ (() => parse(CheckAttemptNumber, () => new RangeError("Check receipt attempt number must be positive.")))();
const evidenceArray = /* @__PURE__ */ (() => parse(IdentityArrayInput.check(Schema.isMaxLength(100)), "Check receipt evidence must be a bounded array."))();
const uniqueEvidence = /* @__PURE__ */ (() => parse(UniqueIdentityValues, "Check receipt evidence ids must be unique."))();
const completionOrder = /* @__PURE__ */ (() => parse(Schema.Struct({ createdAt: CheckTimestamp, completedAt: CheckTimestamp }).check(
  Schema.makeFilter(value => value.createdAt <= value.completedAt)
), () => new RangeError("Check receipt cannot complete before creation.")))();
const startOrder = /* @__PURE__ */ (() => parse(Schema.Struct({
  createdAt: CheckTimestamp, completedAt: CheckTimestamp, startedAt: Schema.UndefinedOr(CheckTimestamp)
}).check(Schema.makeFilter(value => value.startedAt === undefined ||
  (value.startedAt >= value.createdAt && value.startedAt <= value.completedAt)
)), () => new RangeError("Check receipt startedAt must be between creation and completion.")))();
const executionStart = /* @__PURE__ */ (() => parse(Schema.Struct({
  state: CheckTerminalState, startedAt: Schema.UndefinedOr(CheckTimestamp)
}).check(Schema.makeFilter(value => value.startedAt !== undefined || value.state === "skipped" || value.state === "cancelled")), "An executed terminal check receipt must include startedAt."))();

const objectDigest = (input: JsonObject, label: string) => boundary(Effect.gen(function* () {
  parse(IdentityRecordInput, `${label} must be a JSON object.`)(input);
  return `sha256:${yield* hash(input)}`;
}));

function candidateIdentity(value: unknown): CandidateId {
  sha256Identity(value, "Check candidate id");
  return developmentIdentifier("candidate", value);
}

function treeIdentity(value: unknown): TreeId {
  sha256Identity(value, "Check tree id");
  return developmentIdentifier("tree", value);
}

const dependencyKey = /* @__PURE__ */ (() => parse(DependencyLayerKey, "Check dependency layer key must be a version 1 content key."))();

const buildKey = /* @__PURE__ */ (() => parse(BuildArtifactKey, "Check build artifact key must be a version 1 content key."))();

function toolchainIdentity(input: () => CheckToolchainInput): CheckToolchainIdentity {
  return Object.freeze({
    name: stableText(input().name, "Check toolchain name"),
    version: stableText(input().version, "Check toolchain version"),
    implementationDigest: sha256Identity(
      input().implementationDigest,
      "Check toolchain implementation digest"
    ),
    runtimeImageDigest: sha256Identity(
      input().runtimeImageDigest,
      "Check runtime image digest"
    )
  });
}

function checkToolchain(input: CheckToolchainInput): CheckToolchainIdentity {
  objectInput(input, "Check toolchain");
  exactObjectKeys(
    input,
    ["name", "version", "implementationDigest", "runtimeImageDigest"],
    [],
    "Check toolchain"
  );
  return toolchainIdentity(() => input);
}

const checkScope = (input: CheckScopeInput) => boundary(Effect.gen(function* () {
  objectInput(input, "Check scope");
  exactObjectKeys(
    input,
    ["kind", "selection"],
    [],
    "Check scope"
  );
  return Object.freeze({
    kind: stableText(input.kind, "Check scope kind"),
    selectionDigest: yield* objectDigest(
      input.selection,
      "Check scope selection"
    )
  });
}));

function checkCacheability(input: CheckManifestInput): CheckCacheability {
  checkCacheable(input.cacheable);
  if (input.cacheable) {
    noCacheabilityReason(input.nonCacheableReason);
    return Object.freeze({ cacheable: true });
  }
  return Object.freeze({
    cacheable: false,
    reason: stableText(
      input.nonCacheableReason,
      "Check non-cacheable reason"
    )
  });
}

const createCheckManifestTrace = operation("createCheckManifest");
export const createCheckManifest = (input: CheckManifestInput): Effect.Effect<CheckManifestResult, CheckReceiptError, Digest> => createCheckManifestTrace(() => Effect.gen(function* () {
  const snapshot = inputSnapshot(input, "Check manifest input",
    [
      "candidateId",
      "treeId",
      "definition",
      "definitionVersion",
      "scope",
      "toolchain",
      "dependencyLayerKey",
      "buildArtifactKey",
      "dependencies",
      "fixtures",
      "bindings",
      "environment",
      "policy",
      "isolationPolicy",
      "cacheable"
    ],
    ["nonCacheableReason"]
  );
  const manifest = Object.freeze({
    schemaVersion: CHECK_MANIFEST_SCHEMA_VERSION,
    candidateId: candidateIdentity(snapshot.candidateId),
    treeId: treeIdentity(snapshot.treeId),
    definition: stableText(snapshot.definition, "Check definition"),
    definitionVersion: stableText(
      snapshot.definitionVersion,
      "Check definition version"
    ),
    scope: yield* checkScope(snapshot.scope),
    toolchain: checkToolchain(snapshot.toolchain),
    dependencyLayerKey: dependencyKey(snapshot.dependencyLayerKey),
    buildArtifactKey: buildKey(snapshot.buildArtifactKey),
    dependenciesDigest: yield* objectDigest(
      snapshot.dependencies,
      "Check dependencies"
    ),
    fixturesDigest: yield* objectDigest(snapshot.fixtures, "Check fixtures"),
    bindingsDigest: yield* objectDigest(snapshot.bindings, "Check bindings"),
    environmentDigest: yield* objectDigest(
      snapshot.environment,
      "Check environment"
    ),
    policyDigest: yield* objectDigest(snapshot.policy, "Check policy"),
    isolationPolicyDigest: yield* objectDigest(
      snapshot.isolationPolicy,
      "Check isolation policy"
    ),
    cacheability: checkCacheability(snapshot)
  }) satisfies CheckManifest;
  const { canonicalJson, digest } = yield* manifestFingerprint(() => manifest);
  return Object.freeze({ digest, manifest, canonicalJson });
}));

const timestamp = (value: unknown, label: string): number =>
  parse(CheckTimestamp, () => new RangeError(`${label} must be a non-negative safe integer.`))(value);
const terminalState = /* @__PURE__ */ (() => parse(CheckTerminalState, "Check receipt must have a supported terminal state."))();

function evidenceIdentities(
  inputs: readonly CheckEvidenceInput[]
): CheckEvidenceIdentity[] {
  evidenceArray(inputs);
  const evidence = inputs.map((input) => {
    objectInput(input, "Check receipt evidence entry");
    exactObjectKeys(
      input,
      ["evidenceId", "evidenceType", "contentDigest"],
      [],
      "Check receipt evidence entry"
    );
    return Object.freeze({
      evidenceId: developmentIdentifier("evidence", input.evidenceId),
      evidenceType: stableText(input.evidenceType, "Check evidence type"),
      contentDigest: sha256Identity(
        input.contentDigest,
        "Check evidence content digest"
      )
    });
  });
  evidence.sort((left, right) => compareText(left.evidenceId, right.evidenceId));
  uniqueEvidence(evidence.map(item => item.evidenceId));
  return evidence;
}

function verifiedManifest(value: CheckManifest): CheckManifest {
  objectInput(value, "Check manifest");
  exactObjectKeys(
    value,
    [
      "schemaVersion",
      "candidateId",
      "treeId",
      "definition",
      "definitionVersion",
      "scope",
      "toolchain",
      "dependencyLayerKey",
      "buildArtifactKey",
      "dependenciesDigest",
      "fixturesDigest",
      "bindingsDigest",
      "environmentDigest",
      "policyDigest",
      "isolationPolicyDigest",
      "cacheability"
    ],
    [],
    "Check manifest"
  );
  manifestVersion(value.schemaVersion);
  objectInput(value.scope, "Check manifest scope");
  exactObjectKeys(
    value.scope,
    ["kind", "selectionDigest"],
    [],
    "Check manifest scope"
  );
  objectInput(value.toolchain, "Check manifest toolchain");
  exactObjectKeys(
    value.toolchain,
    ["name", "version", "implementationDigest", "runtimeImageDigest"],
    [],
    "Check manifest toolchain"
  );
  objectInput(value.cacheability, "Check manifest cacheability");
  const cacheability =
    value.cacheability.cacheable === true
      ? (() => {
          exactObjectKeys(
            value.cacheability,
            ["cacheable"],
            [],
            "Check manifest cacheability"
          );
          return Object.freeze({ cacheable: true as const });
        })()
      : (() => {
          exactObjectKeys(
            value.cacheability,
            ["cacheable", "reason"],
            [],
            "Check manifest cacheability"
          );
          nonCacheable(value.cacheability.cacheable);
          return Object.freeze({
            cacheable: false as const,
            reason: stableText(
              value.cacheability.reason,
              "Check non-cacheable reason"
            )
          });
        })();
  return Object.freeze({
    schemaVersion: CHECK_MANIFEST_SCHEMA_VERSION,
    candidateId: candidateIdentity(value.candidateId),
    treeId: treeIdentity(value.treeId),
    definition: stableText(value.definition, "Check definition"),
    definitionVersion: stableText(
      value.definitionVersion,
      "Check definition version"
    ),
    scope: Object.freeze({
      kind: stableText(value.scope.kind, "Check scope kind"),
      selectionDigest: sha256Identity(
        value.scope.selectionDigest,
        "Check scope selection digest"
      )
    }),
    toolchain: toolchainIdentity(() => value.toolchain),
    dependencyLayerKey: dependencyKey(value.dependencyLayerKey),
    buildArtifactKey: buildKey(value.buildArtifactKey),
    dependenciesDigest: sha256Identity(
      value.dependenciesDigest,
      "Check dependencies digest"
    ),
    fixturesDigest: sha256Identity(value.fixturesDigest, "Check fixtures digest"),
    bindingsDigest: sha256Identity(value.bindingsDigest, "Check bindings digest"),
    environmentDigest: sha256Identity(
      value.environmentDigest,
      "Check environment digest"
    ),
    policyDigest: sha256Identity(value.policyDigest, "Check policy digest"),
    isolationPolicyDigest: sha256Identity(
      value.isolationPolicyDigest,
      "Check isolation policy digest"
    ),
    cacheability
  });
}

const verifyCheckManifestResultTrace = operation("verifyCheckManifestResult");
export const verifyCheckManifestResult = (result: CheckManifestResult): Effect.Effect<CheckManifestResult, CheckReceiptError, Digest> => verifyCheckManifestResultTrace(() => Effect.gen(function* () {
  const snapshot = inputSnapshot(result, "Check manifest result",
    ["digest", "manifest", "canonicalJson"],
    [], "Check receipt manifest result"
  );
  const manifest = verifiedManifest(snapshot.manifest);
  const { canonicalJson, digest } = yield* manifestFingerprint(() => manifest);
  parse(Schema.Struct({ canonicalJson: Schema.Literal(canonicalJson), digest: Schema.Literal(digest) }),
    "Check receipt manifest fingerprint does not match.")(snapshot);
  return Object.freeze({ digest, manifest, canonicalJson });
}));

const createCheckReceiptTrace = operation("createCheckReceipt");
export const createCheckReceipt = (input: CheckReceiptInput): Effect.Effect<CheckReceiptResult, CheckReceiptError, Digest> => createCheckReceiptTrace(() => Effect.gen(function* () {
  const snapshot = inputSnapshot(input, "Check receipt input",
    [
      "manifest",
      "checkRunId",
      "attemptId",
      "attemptNumber",
      "terminalState",
      "evidence",
      "createdAt",
      "completedAt"
    ],
    ["startedAt"]
  );
  const manifestResult = yield* verifyCheckManifestResult(snapshot.manifest);
  const state = terminalState(snapshot.terminalState);
  const createdAt = timestamp(snapshot.createdAt, "Check receipt createdAt");
  const completedAt = timestamp(snapshot.completedAt, "Check receipt completedAt");
  const startedAt =
    snapshot.startedAt === undefined
      ? undefined
      : timestamp(snapshot.startedAt, "Check receipt startedAt");
  completionOrder({ createdAt, completedAt });
  startOrder({ createdAt, completedAt, startedAt });
  executionStart({ state, startedAt });
  const number = attemptNumber(snapshot.attemptNumber);

  const receipt = Object.freeze({
    schemaVersion: CHECK_RECEIPT_SCHEMA_VERSION,
    manifestDigest: manifestResult.digest,
    manifest: manifestResult.manifest,
    checkRunId: developmentIdentifier("check-run", snapshot.checkRunId),
    attemptId: developmentIdentifier("attempt", snapshot.attemptId),
    attemptNumber: number,
    terminalState: state,
    evidence: Object.freeze(evidenceIdentities(snapshot.evidence)),
    createdAt,
    ...(startedAt === undefined ? {} : { startedAt }),
    completedAt
  }) satisfies CheckReceipt;
  const canonicalJson = canonicalJsonStringify(receipt);
  const digest =
    decodeCheckReceiptDigest(`check-receipt:v${CHECK_RECEIPT_SCHEMA_VERSION}:sha256:${yield* hash(receipt)}`);
  return Object.freeze({ digest, receipt, canonicalJson });
}));

/** Recreate and verify a receipt before using data loaded from durable storage. */
const verifyCheckReceiptResultTrace = operation("verifyCheckReceiptResult");
export const verifyCheckReceiptResult = (result: CheckReceiptResult): Effect.Effect<CheckReceiptResult, CheckReceiptError, Digest> => verifyCheckReceiptResultTrace(() => Effect.gen(function* () {
  const snapshot = inputSnapshot(result, "Stored check receipt result",
    ["digest", "receipt", "canonicalJson"]
  );
  objectInput(snapshot.receipt, "Stored check receipt");
  exactObjectKeys(
    snapshot.receipt,
    [
      "schemaVersion",
      "manifestDigest",
      "manifest",
      "checkRunId",
      "attemptId",
      "attemptNumber",
      "terminalState",
      "evidence",
      "createdAt",
      "completedAt"
    ],
    ["startedAt"],
    "Stored check receipt"
  );
  receiptVersion(snapshot.receipt.schemaVersion);
  const { canonicalJson: manifestCanonicalJson, digest: manifestDigest } = yield* manifestFingerprint(() => snapshot.receipt.manifest);
  const manifest = yield* verifyCheckManifestResult({
    digest: manifestDigest,
    manifest: snapshot.receipt.manifest,
    canonicalJson: manifestCanonicalJson
  });
  parse(Schema.Literal(manifest.digest),
    "Stored check receipt manifest binding does not match.")(snapshot.receipt.manifestDigest);
  const recreated = yield* createCheckReceipt({
    manifest,
    checkRunId: snapshot.receipt.checkRunId,
    attemptId: snapshot.receipt.attemptId,
    attemptNumber: snapshot.receipt.attemptNumber,
    terminalState: snapshot.receipt.terminalState,
    evidence: snapshot.receipt.evidence,
    createdAt: snapshot.receipt.createdAt,
    ...(snapshot.receipt.startedAt === undefined
      ? {}
      : { startedAt: snapshot.receipt.startedAt }),
    completedAt: snapshot.receipt.completedAt
  });
  parse(Schema.Struct({ canonicalJson: Schema.Literal(recreated.canonicalJson), digest: Schema.Literal(recreated.digest) }),
    "Stored check receipt fingerprint does not match.")(snapshot);
  return recreated;
}));
